// What the Import files button does once pressed: save every file that's on through the
// existing mutations (importSave.ts) and sum up what happened.
// Kept out of the component so the dialog only sequences; the decisions are in utils/import.

import graphClient from '../../utils/graphql/graph_client';
import { CreateRecording } from '../../utils/graphql/recorded-trail-graph';
import { CreateHikeWithPlannedTrails } from '../../utils/graphql/hikes-graph';
import { CreateWaypointGroup, CreateWaypoints } from '../../utils/graphql/waypoints-graph';
import { getElevationData } from '../../utils/helpers/HiikerElevationApiHelper';
import { willImport, type ImportCounts, type ImportScreenState } from '../../utils/import/importScreenState';
import { saveImports, type ImportCreated, type ImportSaveDeps, type ImportSavedResult } from '../../utils/import/importSave';
import type { Translate } from '../../utils/import/importTexts';

export type { ImportSavedResult };

export type ImportOutcome = { kind: 'saved' } & ImportSavedResult;

/** The counts of what was actually created, for the result. */
export function createdCounts(created: ImportCreated[]): ImportCounts {
  return {
    recordings: created.filter(c => c.destination === 'recordings').length,
    plannedHikes: created.filter(c => c.destination === 'plannedHikes').length,
    waypointGroups: created.filter(c => c.destination === 'waypoints').length
  };
}

export function saveDeps(t: Translate): ImportSaveDeps {
  return {
    request: (document, variables) => graphClient.request(document as any, variables),
    elevation: coords => getElevationData(coords),
    mutations: {
      createRecording: CreateRecording,
      createHike: CreateHikeWithPlannedTrails,
      createWaypoints: CreateWaypoints,
      createWaypointGroup: CreateWaypointGroup
    },
    texts: {
      defaultTitle: t('import_files.default_title'),
      unnamedWaypoint: t('waypoint_dialog.waypoint_options_dialog_module.imported_waypoint'),
      unnamedHighlight: (n: number) => t('import_files.imported_highlight', { n })
    }
  };
}

/** The button: saves every importing file, one at a time, and sums up what happened. */
export async function runImport(s: ImportScreenState, t: Translate, deps: ImportSaveDeps = saveDeps(t)): Promise<ImportOutcome> {
  const { created, failures } = await saveImports(s.entries.filter(willImport), deps);
  return { kind: 'saved', created, failures, counts: createdCounts(created), notImported: s.entries.length - created.length };
}
